<template>
  <!-- Presentación de una línea de compra (caja, bolsa, bulto) —
       pos-graciela-api/docs/SPEC_PRESENTACIONES_EN_COMPRAS_v0.1.md §12:
       RF-1/RF-2 (una sola forma en las cuatro vías: «Unidad» + las de compra
       del producto), RF-4 (alta desde la línea, con `crear_productos`) y
       RF-7/D5 (propuesta con `posible_presentacion`, sin elegirla sola). -->
  <div v-if="productoId && (opciones.length || puedeCrear)" class="selector-presentacion" :data-testid="testid">
    <div class="d-flex gap-1 align-items-center">
      <select
        class="form-select form-select-sm"
        :value="modelValue ? String(modelValue.presentacion_id) : ''"
        :disabled="disabled"
        :data-testid="`${testid}-select`"
        @change="elegir($event.target.value)"
      >
        <option value="">Unidad</option>
        <option
          v-for="o in opciones"
          :key="o.presentacion_id"
          :value="String(o.presentacion_id)"
          :disabled="o.deshabilitada"
        >
          {{ o.deshabilitada ? `${o.nombre} — no se usa para comprar` : `${o.nombre} (${o.factor} u.)` }}
        </option>
      </select>
      <button
        v-if="puedeCrear && !disabled"
        type="button"
        class="btn btn-outline-secondary btn-sm text-nowrap"
        :data-testid="`${testid}-nueva`"
        @click="abrirNueva"
      >
        + Nueva
      </button>
    </div>

    <small v-if="cargando" class="text-muted">Buscando presentaciones…</small>
    <small v-if="errorLista" class="text-danger d-block">{{ errorLista }}</small>

    <div
      v-if="sugerida && !modelValue && !sugerenciaDescartada && !disabled"
      class="sugerencia"
      :data-testid="`${testid}-sugerencia`"
    >
      <i class="bi bi-box-seam"></i>
      ¿Viene por <strong>{{ sugerida.nombre }}</strong> ({{ sugerida.factor }} u.)?
      <button type="button" class="btn btn-link btn-sm p-0 ms-1" :data-testid="`${testid}-usar-sugerida`" @click="emitir(sugerida)">Usar</button>
      <button type="button" class="btn btn-link btn-sm p-0 ms-1 text-muted" @click="sugerenciaDescartada = true">No</button>
    </div>

    <div v-if="nueva" class="nueva-presentacion" :data-testid="`${testid}-form`">
      <div class="d-flex flex-wrap gap-1">
        <input
          v-model.trim="nueva.nombre"
          type="text"
          class="form-control form-control-sm nombre"
          placeholder="Nombre (Caja x 12)"
          :data-testid="`${testid}-form-nombre`"
          @keydown.enter.prevent
        />
        <input
          v-model.number="nueva.factor"
          type="number"
          min="1"
          step="1"
          class="form-control form-control-sm factor"
          placeholder="Unidades"
          :data-testid="`${testid}-form-factor`"
          @input="sugerirNombre"
          @keydown.enter.prevent
        />
        <input
          v-model.trim="nueva.codigo_barras"
          type="text"
          class="form-control form-control-sm codigo"
          placeholder="Código de barras (opcional)"
          @keydown.enter.prevent
        />
      </div>
      <small v-if="avisoFactor" class="text-danger d-block" :data-testid="`${testid}-form-aviso`">{{ avisoFactor }}</small>
      <small v-else-if="nueva.factor > 0" class="text-muted d-block">1 {{ nueva.nombre || 'presentación' }} = {{ nueva.factor }} unidades de {{ productoNombre }}</small>
      <small v-if="errorAlta" class="text-danger d-block" :data-testid="`${testid}-form-error`">{{ errorAlta }}</small>
      <div class="d-flex gap-1 mt-1">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          :disabled="!puedeGuardarNueva || guardando"
          :data-testid="`${testid}-form-crear`"
          @click="crear"
        >
          {{ guardando ? 'Creando…' : 'Crear y usar' }}
        </button>
        <button type="button" class="btn btn-outline-secondary btn-sm" @click="nueva = null">Cancelar</button>
      </div>
    </div>
  </div>
</template>

<script setup>
/* global defineEmits, defineProps */
import { computed, ref, watch } from 'vue'
import apiService from '@/services/apiService'
import productPresentationsService from '@/services/productPresentationsService'
import { hasPermission } from '@/services/authSessionService'
import { opcionesPresentacion, presentacionDeCompra, presentacionSugerida, nombreSugerido } from '@/utils/presentacionCompra.mjs'
import { esFactorEntero, mensajeFactorNoEntero } from '@/utils/presentacionFactor.mjs'

const props = defineProps({
  productoId: { type: [Number, String], default: null },
  productoNombre: { type: String, default: '' },
  modelValue: { type: Object, default: null },
  // Avisos del agente de la línea (compra desde foto), para D5.
  avisos: { type: Array, default: () => [] },
  disabled: { type: Boolean, default: false },
  testid: { type: String, default: 'presentacion' }
})
const emit = defineEmits(['update:modelValue'])

const opciones = ref([])
const cargando = ref(false)
const errorLista = ref('')
const sugerenciaDescartada = ref(false)
const nueva = ref(null)
const guardando = ref(false)
const errorAlta = ref('')

// D2: el mismo permiso que el alta en Inventario › Presentaciones.
const puedeCrear = computed(() => hasPermission('crear_productos'))
const sugerida = computed(() => presentacionSugerida(opciones.value, props.avisos))

const avisoFactor = computed(() => {
  if (!nueva.value || nueva.value.factor === '' || nueva.value.factor === null) return ''
  if (esFactorEntero(nueva.value.factor) && Number(nueva.value.factor) > 0) return ''
  return mensajeFactorNoEntero({
    presentacion: nueva.value.nombre || 'La presentación',
    cantidad: nueva.value.factor,
    producto: props.productoNombre || 'el producto'
  })
})
const puedeGuardarNueva = computed(() => Boolean(
  nueva.value?.nombre && Number(nueva.value.factor) > 1 && !avisoFactor.value
))

async function cargar() {
  opciones.value = []
  errorLista.value = ''
  sugerenciaDescartada.value = false
  if (!props.productoId) return
  cargando.value = true
  try {
    const lista = await productPresentationsService.list({ producto_id: props.productoId })
    opciones.value = opcionesPresentacion(lista)
  } catch (error) {
    errorLista.value = 'No se pudieron leer las presentaciones del producto.'
  } finally {
    cargando.value = false
  }
}
watch(() => props.productoId, cargar, { immediate: true })

function emitir(presentacion) {
  emit('update:modelValue', presentacion ? { ...presentacion } : null)
}

function elegir(valor) {
  if (!valor) return emitir(null)
  const opcion = opciones.value.find((o) => String(o.presentacion_id) === String(valor))
  if (opcion && !opcion.deshabilitada) emitir(opcion)
}

function abrirNueva() {
  errorAlta.value = ''
  nueva.value = { nombre: '', factor: null, codigo_barras: '' }
}

function sugerirNombre() {
  if (!nueva.value) return
  const actual = nueva.value.nombre
  if (!actual || /^Caja( x \d+)?$/.test(actual)) nueva.value.nombre = nombreSugerido(nueva.value.factor)
}

async function crear() {
  if (!puedeGuardarNueva.value) return
  guardando.value = true
  errorAlta.value = ''
  try {
    // La unidad del componente tiene que ser la del producto (el API la valida).
    const { data: producto } = await apiService.get(`/api/products/${props.productoId}`)
    const creada = await productPresentationsService.create({
      nombre: nueva.value.nombre,
      tipo_presentacion: 'CONVERSION',
      codigo_barras: nueva.value.codigo_barras || null,
      es_presentacion_compra: true,
      es_presentacion_venta: false,
      precio_propio_habilitado: false,
      activa: true,
      precios: [],
      componentes: [{
        producto_id: Number(props.productoId),
        cantidad_operativa: Number(nueva.value.factor),
        unidad_medida_id: Number(producto?.unidad_medida_id),
        orden: 1,
        activo: true
      }]
    })
    const full = await productPresentationsService.get(creada.presentacion_id)
    const lista = presentacionDeCompra(full)
    await cargar()
    nueva.value = null
    if (!lista.error) emitir({ ...lista, deshabilitada: false, motivo: null })
  } catch (error) {
    const data = error?.response?.data || {}
    errorAlta.value = data.error || data.message || 'No se pudo crear la presentación.'
  } finally {
    guardando.value = false
  }
}
</script>

<style scoped>
.selector-presentacion { margin-top: 0.25rem; }
.sugerencia { font-size: 0.8rem; margin-top: 0.2rem; color: var(--bs-info-text-emphasis, #055160); }
.nueva-presentacion { margin-top: 0.35rem; padding: 0.4rem; border: 1px dashed var(--bs-border-color, #ced4da); border-radius: 0.35rem; }
.nueva-presentacion .nombre { flex: 2 1 10rem; }
.nueva-presentacion .factor { flex: 0 1 6rem; }
.nueva-presentacion .codigo { flex: 1 1 9rem; }
</style>
